// =============================================================
// VARIANCE — The Executive Command Center
// File      : Cockpit.tsx  (PART 1 of 2)
// Location  : /home/Applications/variance/web/src/pages/Cockpit.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Premium CEO Dashboard with Real Data, Help & Slide-outs.
//             Part 1: Imports, interfaces, state, fetch wiring,
//             header, KPI strip, 3 main department panels, functional
//             grid (with live Finance panel). Part 2 delivers the
//             9-pillar table, charts, and all drill-down slide-outs
//             (including Finance drill-down) plus closing tags.
// =============================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, AlertTriangle, Eye, CheckCircle, Building2, FolderKanban, X, BarChart3, Banknote, FileText } from 'lucide-react';
import { PageHelp, HelpTooltip } from '../components/HelpSystem';
import { AlignmentChart } from '../components/AlignmentChart';
import ReactECharts from 'echarts-for-react';

interface KpiData {
  companies: number;
  projects: number;
  achievement: string;
  deviations: number;
  watch: number;
  onTrack: number;
}

interface PortfolioRow {
  company_id: string;
  company_name: string;
  company_code: string;
  scores: number[];
  overall: string;
}

function getRagStatus(score: number) {
  if (score >= 80) return 'green';
  if (score >= 50) return 'amber';
  return 'red';
}

function getRagColor(status: string) {
  if (status === 'green') return 'var(--rag-green, #22C55E)';
  if (status === 'amber') return 'var(--rag-amber, #F59E0B)';
  if (status === 'red')   return 'var(--rag-red, #EF4444)';
  return 'var(--rag-grey, #64748B)';
}

function KpiTile({ title, value, icon, color }: { title: string, value: string, icon: React.ReactNode, color: string }) {
  return (
    <div className="p-4 rounded-xl flex flex-col justify-between shadow-sm" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{title}</span>
        <span style={{ color }}>{icon}</span>
      </div>
      <div className="text-3xl font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

export function Cockpit() {
  const navigate = useNavigate();

  const [kpis, setKpis] = useState<KpiData | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Main Department Panels
  const [constructionStats, setConstructionStats] = useState<any>(null);
  const [salesStats, setSalesStats] = useState<any>(null);
  const [collectionStats, setCollectionStats] = useState<any>(null);
  const [trendData, setTrendData] = useState<any[]>([]);

  // Functional Department Panels
  const [assetsStats, setAssetsStats] = useState<any>(null);
  const [financeStats, setFinanceStats] = useState<any>(null);

  // Drill-Down Data
  const [constructionDetails, setConstructionDetails] = useState<any[]>([]);
  const [salesDetails, setSalesDetails] = useState<any[]>([]);
  const [collectionDetails, setCollectionDetails] = useState<any[]>([]);
  const [assetsDetails, setAssetsDetails] = useState<any[]>([]);
  const [financeDetails, setFinanceDetails] = useState<any[]>([]);

  // Drill Down Panels
  const [drillDownType, setDrillDownType] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<PortfolioRow | null>(null);
  // --- Chart Expansion State ---
  const [expandedChart, setExpandedChart] = useState<string | null>(null);

  const pillars = ['Assets', 'Design', 'Approvals', 'Execution', 'Sales', 'CRM', 'Collections', 'HR', 'Finance'];

  // TODO: When CogniFleet AUTH is integrated, add a check for the user's license_key.
  // If the license includes 'assetpro', replace the Assets panel button with an
  // "Open AssetPro →" launcher that SSO-redirects to assetpro.cognifleet.in.

  useEffect(() => {
    Promise.all([
      fetch('/api/cockpit/kpis').then(res => res.json()),
      fetch('/api/cockpit/portfolio').then(res => res.json()),
      fetch('/api/cockpit/construction-summary').then(res => res.json()),
      fetch('/api/cockpit/sales-summary').then(res => res.json()),
      fetch('/api/cockpit/collections-summary').then(res => res.json()),
      fetch('/api/cockpit/financial-trend').then(res => res.json()),
      fetch('/api/cockpit/drilldown-construction').then(res => res.json()),
      fetch('/api/cockpit/drilldown-sales').then(res => res.json()),
      fetch('/api/cockpit/drilldown-collections').then(res => res.json()),
      fetch('/api/cockpit/assets-summary').then(res => res.json()),
      fetch('/api/cockpit/drilldown-assets').then(res => res.json()),
      fetch('/api/cockpit/finance-summary').then(res => res.json()),
      fetch('/api/cockpit/drilldown-finance').then(res => res.json())
    ]).then(([kpiData, portfolioData, constData, salesData, collData, trendRes, constDetails, salesDetailsRes, collDetails, assetsSummary, assetsDetailsRes, financeSummaryRes, financeDetailsRes]) => {
      if (!kpiData.error) setKpis(kpiData);
      if (!portfolioData.error && Array.isArray(portfolioData)) setPortfolio(portfolioData);
      if (!constData.error) setConstructionStats(constData);
      if (!salesData.error) setSalesStats(salesData);
      if (!collData.error) setCollectionStats(collData);
      if (!trendRes.error) setTrendData(trendRes);
      if (!constDetails.error) setConstructionDetails(constDetails);
      if (!salesDetailsRes.error) setSalesDetails(salesDetailsRes);
      if (!collDetails.error) setCollectionDetails(collDetails);
      if (!assetsSummary.error) setAssetsStats(assetsSummary);
      if (!assetsDetailsRes.error) setAssetsDetails(assetsDetailsRes);
      if (financeSummaryRes && !financeSummaryRes.error) setFinanceStats(financeSummaryRes);
      if (Array.isArray(financeDetailsRes) && !financeDetailsRes.error) setFinanceDetails(financeDetailsRes);
      else if (financeDetailsRes && Array.isArray(financeDetailsRes.projects)) setFinanceDetails(financeDetailsRes.projects);
      setIsLoading(false);
    }).catch(err => {
      console.error("Failed to fetch cockpit data", err);
      setIsLoading(false);
    });
  }, []);

  if (isLoading) return <div className="p-10 text-white">Loading Cockpit...</div>;

  return (
    <div className="space-y-6 max-w-[1600px] relative min-h-screen pt-4">

      <PageHelp
        title="The Macro Vantage"
        description="This is your bird's-eye view. Monitor the 3 Perspectives across all companies and the 9 Pillars. Click any row or panel to drill down into the details."
      />

      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight text-white">The Management Cockpit</h1>
          <p className="text-sm text-gray-400 mt-2">
            The BI Command Center for the CEO & Board. Review and track planned versus actual across every key area, identify deviations, focus on what matters & act on what counts.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <KpiTile title="Companies" value={kpis?.companies.toString() || '0'} icon={<Building2 size={20} />} color="var(--brand-accent)" />
          <KpiTile title="Projects" value={kpis?.projects.toString() || '0'} icon={<FolderKanban size={20} />} color="var(--brand-accent)" />
          <KpiTile title="Achievement" value={kpis?.achievement || '0%'} icon={<TrendingUp size={20} />} color="var(--text-primary)" />
          <KpiTile title="Deviations" value={kpis?.deviations.toString() || '0'} icon={<AlertTriangle size={20} />} color="#EF4444" />
          <KpiTile title="Watch" value={kpis?.watch.toString() || '0'} icon={<Eye size={20} />} color="#F59E0B" />
          <KpiTile title="On Track" value={kpis?.onTrack.toString() || '0'} icon={<CheckCircle size={20} />} color="#22C55E" />
        </div>
      </div>

      {/* 3 MAIN DEPARTMENT PANELS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* Construction Panel */}
        <div onClick={() => setDrillDownType('construction')} className="p-6 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-white group-hover:text-[var(--brand-accent)] flex items-center gap-2">
              Construction <HelpTooltip text="Aggregate physical progress vs planned/actual costs across all construction milestones in the group." />
            </h3>
            <span className="text-xs text-gray-500">Plan vs Actual</span>
          </div>
          {constructionStats ? (
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Progress</span>
                <span className="font-mono text-white font-bold">{constructionStats.progress.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-[var(--bg-canvas)] h-2 rounded-full overflow-hidden">
                <div className="h-full bg-[var(--brand-accent)]" style={{ width: `${constructionStats.progress}%` }} />
              </div>
              <div className="flex justify-between text-xs mt-4">
                <div><span className="text-gray-500 block">Planned</span><span className="text-white font-mono">₹{(constructionStats.planned / 10000000).toFixed(2)}Cr</span></div>
                <div className="text-right"><span className="text-gray-500 block">Actual</span><span className="text-orange-400 font-mono">₹{(constructionStats.actual / 10000000).toFixed(2)}Cr</span></div>
              </div>
            </div>
          ) : <div className="text-gray-500 text-sm">No data</div>}
        </div>

        {/* Sales Panel */}
        <div onClick={() => setDrillDownType('sales')} className="p-6 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-white group-hover:text-[var(--brand-accent)] flex items-center gap-2">
              Sales <HelpTooltip text="Total value of all booked units across the enterprise and the ratio of sold vs available units." />
            </h3>
            <span className="text-xs text-gray-500">Booked Value</span>
          </div>
          {salesStats ? (
            <div className="space-y-3">
              <div>
                <span className="text-gray-400 text-sm block mb-1">Total Booked</span>
                <span className="font-mono text-white text-2xl font-bold">₹{(salesStats.totalBooked / 10000000).toFixed(2)}Cr</span>
              </div>
              <div className="flex justify-between text-xs mt-4 pt-3 border-t border-[var(--border-subtle)]">
                <div><span className="text-gray-500 block">Units Sold</span><span className="text-green-400 font-mono font-bold">{salesStats.unitsSold}</span></div>
                <div className="text-right"><span className="text-gray-500 block">Available</span><span className="text-gray-300 font-mono">{salesStats.unitsAvailable}</span></div>
              </div>
            </div>
          ) : <div className="text-gray-500 text-sm">No data</div>}
        </div>

        {/* Collections Panel */}
        <div onClick={() => setDrillDownType('collections')} className="p-6 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-white group-hover:text-[var(--brand-accent)] flex items-center gap-2">
              Collections <HelpTooltip text="Cash actually received from customers vs the outstanding receivable balance." />
            </h3>
            <span className="text-xs text-gray-500">Cash Position</span>
          </div>
          {collectionStats ? (
            <div className="space-y-3">
              <div>
                <span className="text-gray-400 text-sm block mb-1">Collected</span>
                <span className="font-mono text-green-400 text-2xl font-bold">₹{(collectionStats.collected / 10000000).toFixed(2)}Cr</span>
              </div>
              <div className="flex justify-between text-xs mt-4 pt-3 border-t border-[var(--border-subtle)]">
                <div><span className="text-gray-500 block">Outstanding</span><span className="text-red-400 font-mono">₹{(collectionStats.outstanding / 10000000).toFixed(2)}Cr</span></div>
                <div className="text-right"><span className="text-gray-500 block">Rate</span><span className="text-white font-mono">{collectionStats.collectionRate}%</span></div>
              </div>
            </div>
          ) : <div className="text-gray-500 text-sm">No data</div>}
        </div>

      </div>

      {/* FUNCTIONAL DEPARTMENTS GRID */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">

        {/* Assets Panel */}
        <div onClick={() => setDrillDownType('assets')} className="p-4 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <h3 className="text-sm font-bold text-white group-hover:text-[var(--brand-accent)] mb-3 flex items-center gap-1">
            Assets & Properties <HelpTooltip text="Fixed Assets, Land Bank, and Liabilities." />
          </h3>
          {assetsStats ? (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-gray-400"><span>Count</span><span className="text-white font-mono font-bold">{assetsStats.totalAssets}</span></div>
              <div className="flex justify-between text-xs text-gray-400"><span>Value</span><span className="text-green-400 font-mono">₹{(assetsStats.totalValue/10000000).toFixed(2)}Cr</span></div>
              <div className="flex justify-between text-xs text-gray-400"><span>Balance</span><span className="text-red-400 font-mono">₹{(assetsStats.balanceToPay/10000000).toFixed(2)}Cr</span></div>
            </div>
          ) : <div className="text-gray-500 text-xs">Loading...</div>}
        </div>

        {/* HR Placeholder */}
        <div className="p-4 rounded-xl border flex flex-col justify-between" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <h3 className="text-sm font-bold text-gray-500 mb-3">HR</h3>
          <div className="text-xs text-gray-600">Coming Soon</div>
        </div>

        {/* Finance Panel (LIVE) */}
        <div onClick={() => setDrillDownType('finance')} className="p-4 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <h3 className="text-sm font-bold text-white group-hover:text-[var(--brand-accent)] mb-3 flex items-center gap-1">
            Finance <HelpTooltip text="Portfolio budget, invoiced, and paid totals. Click to drill into project-wise financials." />
          </h3>
          {financeStats ? (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-gray-400"><span>Budget</span><span className="text-white font-mono font-bold">₹{(Number(financeStats.totalBudget || 0)/10000000).toFixed(2)}Cr</span></div>
              <div className="flex justify-between text-xs text-gray-400"><span>Invoiced</span><span className="text-orange-400 font-mono">₹{(Number(financeStats.totalInvoiced || 0)/10000000).toFixed(2)}Cr</span></div>
              <div className="flex justify-between text-xs text-gray-400"><span>Paid</span><span className="text-green-400 font-mono">₹{(Number(financeStats.totalPaid || 0)/10000000).toFixed(2)}Cr</span></div>
            </div>
          ) : <div className="text-gray-500 text-xs">Loading...</div>}
        </div>

        {/* Approvals Placeholder */}
        <div className="p-4 rounded-xl border flex flex-col justify-between" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <h3 className="text-sm font-bold text-gray-500 mb-3">Approvals</h3>
          <div className="text-xs text-gray-600">Coming Soon</div>
        </div>

        {/* Compliance Placeholder */}
        <div className="p-4 rounded-xl border flex flex-col justify-between" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <h3 className="text-sm font-bold text-gray-500 mb-3">Compliance</h3>
          <div className="text-xs text-gray-600">Coming Soon</div>
        </div>

      </div>

      {/* 9-PILLAR TABLE */}
      <div className="p-6 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            Portfolio Health Scorecard <HelpTooltip text="A RAG (Red/Amber/Green) snapshot of all 9 pillars across every company. Click any row to see detailed scores." />
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="p-3 text-xs font-semibold uppercase text-gray-400">Company</th>
                {pillars.map(p => <th key={p} className="p-3 text-xs font-semibold uppercase text-gray-400 text-center">{p}</th>)}
                <th className="p-3 text-xs font-semibold uppercase text-gray-400 text-right">Overall</th>
              </tr>
            </thead>
            <tbody>
              {portfolio.map((row) => (
                <tr
                  key={row.company_id}
                  onClick={() => setSelectedCompany(row)}
                  className="border-t hover:bg-[var(--bg-panel-alt)] cursor-pointer transition-colors group"
                  style={{ borderColor: 'var(--border-subtle)' }}
                >
                  <td className="p-3 text-sm font-bold group-hover:text-[var(--brand-accent)] text-white">{row.company_name}</td>
                  {row.scores.map((score, i) => (
                    <td key={i} className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getRagColor(getRagStatus(score)) }} />
                        <span className="text-sm font-mono text-white">{Math.round(score)}%</span>
                      </div>
                    </td>
                  ))}
                  <td className="p-3 text-right">
                    <span className="text-xs font-bold px-2 py-1 rounded" style={{ color: getRagColor(getRagStatus(row.scores.reduce((a,b)=>a+b,0)/9)) }}>{row.overall}</span>
                  </td>
                </tr>
              ))}
              {portfolio.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-gray-500">No companies found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* CHARTS SECTION */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        
        {/* EXECUTION VS REVENUE CHART */}
        <div 
          onClick={() => setExpandedChart('alignment')}
          className="p-6 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group relative"
          style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              Execution vs Revenue (Group) <HelpTooltip text="Compares what we spent on construction vs what we collected from sales. A gap indicates cash flow risk." />
            </h2>
            <span className="text-xs text-gray-500 group-hover:text-[var(--brand-accent)]">Click to expand</span>
          </div>
          <div className="min-h-[350px]">
            <AlignmentChart milestones={[
              { milestone_type: 'Construction Milestone', planned_amount: constructionStats?.planned || 0, actual_amount: constructionStats?.actual || 0, weightage_pct: 100, physical_progress_pct: constructionStats?.progress || 0 },
              { milestone_type: 'Revenue Milestone', planned_amount: salesStats?.totalBooked || 0, actual_amount: collectionStats?.collected || 0, weightage_pct: 100, physical_progress_pct: (Number(collectionStats?.collectionRate) || 0) }
            ]} />
          </div>
        </div>

        {/* FINANCIAL TREND CHART */}
        <div 
          onClick={() => setExpandedChart('trend')}
          className="p-6 rounded-xl border cursor-pointer hover:border-[var(--brand-accent)] transition-colors group relative"
          style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              Financial Trend (Group) <HelpTooltip text="Monthly cash inflow (collections) vs outflow (construction spending) across the entire enterprise." />
            </h2>
            <span className="text-xs text-gray-500 group-hover:text-[var(--brand-accent)]">Click to expand</span>
          </div>
          <div className="min-h-[400px]">
             <ReactECharts 
              option={{
                backgroundColor: 'transparent',
                tooltip: { trigger: 'axis', backgroundColor: '#1A2540', borderColor: '#22304D', textStyle: { color: '#F1F5FB' }, valueFormatter: (val: number) => `₹${(val / 100000).toFixed(1)}L` },
                legend: { 
                  data: ['Inflow (Collections)', 'Outflow (Construction)'], 
                  textStyle: { color: '#94A3B8', fontSize: 14 }, 
                  bottom: -5,
                  itemGap: 30 
                },
                grid: { left: '3%', right: '4%', bottom: '20%', top: '10%', containLabel: true },
                xAxis: { 
                  type: 'category', 
                  data: trendData.map(d => d.month), 
                  axisLabel: { color: '#94A3B8', fontSize: 11, margin: 12 }, 
                  axisLine: { lineStyle: { color: '#22304D' } } 
                },
                yAxis: { 
                  type: 'value', 
                  axisLabel: { color: '#94A3B8', fontSize: 12, formatter: (val: number) => `${(val / 100000).toFixed(0)}L` }, 
                  splitLine: { lineStyle: { color: '#22304D', type: 'dashed' } } 
                },
                series: [
                  { name: 'Inflow (Collections)', type: 'bar', barWidth: '25%', itemStyle: { color: '#22C55E', borderRadius: [4, 4, 0, 0] }, data: trendData.map(d => d.inflow) },
                  { name: 'Outflow (Construction)', type: 'bar', barWidth: '25%', itemStyle: { color: '#F97316', borderRadius: [4, 4, 0, 0] }, data: trendData.map(d => d.outflow) }
                ]
              }} 
              style={{ height: '100%', width: '100%' }} 
            />
          </div>
        </div>
      </div>

      {/* --- EXPANDED CHART MODAL --- */}
      {expandedChart && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm p-8">
          <div className="w-full max-w-6xl h-[80vh] bg-[var(--bg-panel)] border border-[var(--border-subtle)] rounded-xl p-8 flex flex-col shadow-2xl animate-in zoom-in-95 duration-200 relative">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-white capitalize">
                {expandedChart === 'alignment' ? 'Execution vs Revenue' : 'Financial Trend'}
              </h2>
              <button onClick={() => setExpandedChart(null)} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-[var(--bg-panel-alt)]">
                <X size={28} />
              </button>
            </div>
            <div className="flex-1">
              {expandedChart === 'alignment' && (
                <AlignmentChart milestones={[
                  { milestone_type: 'Construction Milestone', planned_amount: constructionStats?.planned || 0, actual_amount: constructionStats?.actual || 0, weightage_pct: 100, physical_progress_pct: constructionStats?.progress || 0 },
                  { milestone_type: 'Revenue Milestone', planned_amount: salesStats?.totalBooked || 0, actual_amount: collectionStats?.collected || 0, weightage_pct: 100, physical_progress_pct: (Number(collectionStats?.collectionRate) || 0) }
                ]} />
              )}
              {expandedChart === 'trend' && (
                <ReactECharts 
                  option={{
                    backgroundColor: 'transparent',
                    tooltip: { trigger: 'axis', backgroundColor: '#1A2540', borderColor: '#22304D', textStyle: { color: '#F1F5FB' }, valueFormatter: (val: number) => `₹${(val / 100000).toFixed(1)}L` },
                    legend: { data: ['Inflow (Collections)', 'Outflow (Construction)'], textStyle: { color: '#94A3B8', fontSize: 16 }, bottom: 10 },
                    grid: { left: '3%', right: '4%', bottom: '10%', top: '5%', containLabel: true },
                    xAxis: { type: 'category', data: trendData.map(d => d.month), axisLabel: { color: '#94A3B8', fontSize: 14 }, axisLine: { lineStyle: { color: '#22304D' } } },
                    yAxis: { type: 'value', axisLabel: { color: '#94A3B8', fontSize: 14, formatter: (val: number) => `${(val / 100000).toFixed(0)}L` }, splitLine: { lineStyle: { color: '#22304D', type: 'dashed' } } },
                    series: [
                      { name: 'Inflow (Collections)', type: 'bar', barWidth: '25%', itemStyle: { color: '#22C55E', borderRadius: [4, 4, 0, 0] }, data: trendData.map(d => d.inflow) },
                      { name: 'Outflow (Construction)', type: 'bar', barWidth: '25%', itemStyle: { color: '#F97316', borderRadius: [4, 4, 0, 0] }, data: trendData.map(d => d.outflow) }
                    ]
                  }} 
                  style={{ height: '100%', width: '100%' }} 
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* DRILL-DOWN SLIDE-OUT PANEL - Company */}
      {selectedCompany && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setSelectedCompany(null)}></div>
          <div className="w-full md:max-w-md h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-start bg-[var(--bg-panel-alt)]">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--brand-accent)]">Company Drill-Down</span>
                <h2 className="text-xl font-bold text-white mt-1">{selectedCompany.company_name}</h2>
                <p className="text-sm text-gray-400 mt-1">Code: {selectedCompany.company_code}</p>
              </div>
              <button onClick={() => setSelectedCompany(null)} className="p-1 text-gray-400 hover:text-white rounded-full hover:bg-[var(--bg-canvas)] transition-colors"><X size={24} /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <h3 className="text-sm font-bold text-gray-300 mb-4">Detailed Scores</h3>
              <div className="space-y-2">
                {selectedCompany.scores.map((score, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-[var(--bg-panel-alt)] rounded border border-[var(--border-subtle)]">
                    <span className="text-sm text-white">{pillars[idx]}</span>
                    <span className="text-sm font-mono" style={{ color: getRagColor(getRagStatus(score)) }}>{Math.round(score)}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DRILL-DOWN SLIDE-OUT PANEL - Department */}
      {drillDownType && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setDrillDownType(null)}></div>
          <div className="w-full md:max-w-2xl h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">

            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-start bg-[var(--bg-panel-alt)]">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--brand-accent)]">Department Drill-Down</span>
                <h2 className="text-xl font-bold text-white mt-1 capitalize">{drillDownType} Details</h2>
              </div>
              <button onClick={() => setDrillDownType(null)} className="p-1 text-gray-400 hover:text-white rounded-full hover:bg-[var(--bg-canvas)] transition-colors"><X size={24} /></button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">

              {/* CONSTRUCTION DETAILS */}
              {drillDownType === 'construction' && (
                <div>
                  <h3 className="text-sm font-bold text-gray-300 mb-3">Project-wise Construction</h3>
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr>
                        <th className="p-2 text-xs text-gray-400">Project</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Planned</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Actual</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Variance</th>
                        <th className="p-2 text-xs text-gray-400 text-center">Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {constructionDetails.map(d => {
                        const variance = d.actual - d.planned;
                        return (
                          <tr key={d.project_id} onClick={() => navigate(`/projects/${d.project_id}`)} className="border-t cursor-pointer hover:bg-[var(--bg-panel-alt)]" style={{ borderColor: 'var(--border-subtle)' }}>
                            <td className="p-2 text-sm text-white font-medium">{d.project_name}</td>
                            <td className="p-2 text-right text-xs text-gray-300 font-mono">₹{(d.planned/10000000).toFixed(2)}Cr</td>
                            <td className="p-2 text-right text-xs text-gray-300 font-mono">₹{(d.actual/10000000).toFixed(2)}Cr</td>
                            <td className={`p-2 text-right text-xs font-mono ${variance > 0 ? 'text-red-400' : 'text-green-400'}`}>₹{(variance/10000000).toFixed(2)}Cr</td>
                            <td className="p-2 text-center text-xs text-white font-mono">{d.progress.toFixed(1)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* SALES DETAILS */}
              {drillDownType === 'sales' && (
                <div>
                  <h3 className="text-sm font-bold text-gray-300 mb-3">Project-wise Sales (Base + Additional Works)</h3>
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr>
                        <th className="p-2 text-xs text-gray-400">Project</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Base Booked</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Add'l Works</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Grand Total</th>
                        <th className="p-2 text-xs text-gray-400 text-center">Sold / Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesDetails.map(d => (
                        <tr key={d.project_id} onClick={() => navigate(`/sales/bookings`)} className="border-t cursor-pointer hover:bg-[var(--bg-panel-alt)]" style={{ borderColor: 'var(--border-subtle)' }}>
                          <td className="p-2 text-sm text-white font-medium">{d.project_name}</td>
                          <td className="p-2 text-right text-xs text-gray-300 font-mono">₹{(d.base_booked/10000000).toFixed(2)}Cr</td>
                          <td className="p-2 text-right text-xs text-purple-400 font-mono">₹{(d.addl_works/10000000).toFixed(2)}Cr</td>
                          <td className="p-2 text-right text-xs text-white font-bold font-mono">₹{(d.grand_total/10000000).toFixed(2)}Cr</td>
                          <td className="p-2 text-center text-xs text-gray-300 font-mono">{d.units_sold} / {d.units_total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* COLLECTIONS DETAILS */}
              {drillDownType === 'collections' && (
                <div>
                  <h3 className="text-sm font-bold text-gray-300 mb-3">Project-wise Collections</h3>
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr>
                        <th className="p-2 text-xs text-gray-400">Project</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Booked</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Collected</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Outstanding</th>
                        <th className="p-2 text-xs text-gray-400 text-center">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collectionDetails.map(d => (
                        <tr key={d.project_id} onClick={() => navigate(`/sales/bookings`)} className="border-t cursor-pointer hover:bg-[var(--bg-panel-alt)]" style={{ borderColor: 'var(--border-subtle)' }}>
                          <td className="p-2 text-sm text-white font-medium">{d.project_name}</td>
                          <td className="p-2 text-right text-xs text-gray-300 font-mono">₹{(d.booked/10000000).toFixed(2)}Cr</td>
                          <td className="p-2 text-right text-xs text-green-400 font-mono">₹{(d.collected/10000000).toFixed(2)}Cr</td>
                          <td className="p-2 text-right text-xs text-red-400 font-mono">₹{(d.outstanding/10000000).toFixed(2)}Cr</td>
                          <td className="p-2 text-center text-xs text-white font-mono">{d.rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ASSETS DETAILS */}
              {drillDownType === 'assets' && (
                <div>
                  <h3 className="text-sm font-bold text-gray-300 mb-3">Assets & Properties</h3>
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr>
                        <th className="p-2 text-xs text-gray-400">Asset</th>
                        <th className="p-2 text-xs text-gray-400">Company</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Current Value</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assetsDetails.map(a => (
                        <tr key={a.id} onClick={() => navigate('/assets')} className="border-t cursor-pointer hover:bg-[var(--bg-panel-alt)]" style={{ borderColor: 'var(--border-subtle)' }}>
                          <td className="p-2 text-sm text-white font-medium">{a.name} <span className="text-xs text-gray-500">({a.code})</span></td>
                          <td className="p-2 text-xs text-gray-300">{a.company}</td>
                          <td className="p-2 text-right text-xs text-green-400 font-mono">₹{(a.currentValue/10000000).toFixed(2)}Cr</td>
                          <td className="p-2 text-right text-xs text-red-400 font-mono">₹{(a.balance/10000000).toFixed(2)}Cr</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* FINANCE DETAILS (NEW) */}
              {drillDownType === 'finance' && (
                <div>
                  <h3 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-2">
                    Project-wise Finance
                    <HelpTooltip text="Budget, invoiced, and paid per project. Click a row to open the project." />
                  </h3>

                  {/* Summary strip */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="p-2 rounded border" style={{ backgroundColor: 'var(--bg-panel-alt)', borderColor: 'var(--border-subtle)' }}>
                      <div className="text-xs text-gray-400 flex items-center gap-1"><Banknote size={12} /> Budget</div>
                      <div className="text-sm font-mono font-bold text-white">₹{(Number(financeStats?.totalBudget || 0)/10000000).toFixed(2)}Cr</div>
                    </div>
                    <div className="p-2 rounded border" style={{ backgroundColor: 'var(--bg-panel-alt)', borderColor: 'var(--border-subtle)' }}>
                      <div className="text-xs text-gray-400 flex items-center gap-1"><FileText size={12} /> Invoiced</div>
                      <div className="text-sm font-mono font-bold text-orange-400">₹{(Number(financeStats?.totalInvoiced || 0)/10000000).toFixed(2)}Cr</div>
                    </div>
                    <div className="p-2 rounded border" style={{ backgroundColor: 'var(--bg-panel-alt)', borderColor: 'var(--border-subtle)' }}>
                      <div className="text-xs text-gray-400 flex items-center gap-1"><CheckCircle size={12} /> Paid</div>
                      <div className="text-sm font-mono font-bold text-green-400">₹{(Number(financeStats?.totalPaid || 0)/10000000).toFixed(2)}Cr</div>
                    </div>
                  </div>

                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr>
                        <th className="p-2 text-xs text-gray-400">Project</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Budget</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Invoiced</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Paid</th>
                        <th className="p-2 text-xs text-gray-400 text-right">Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financeDetails.map((d: any) => {
                        const budget = Number(d.budget || d.current_budget || 0);
                        const invoiced = Number(d.invoiced || 0);
                        const paid = Number(d.paid || 0);
                        const remaining = budget - invoiced;
                        return (
                          <tr key={d.project_id} onClick={() => navigate(`/projects/${d.project_id}`)} className="border-t cursor-pointer hover:bg-[var(--bg-panel-alt)]" style={{ borderColor: 'var(--border-subtle)' }}>
                            <td className="p-2 text-sm text-white font-medium">{d.project_name}</td>
                            <td className="p-2 text-right text-xs text-gray-300 font-mono">₹{(budget/10000000).toFixed(2)}Cr</td>
                            <td className="p-2 text-right text-xs text-orange-400 font-mono">₹{(invoiced/10000000).toFixed(2)}Cr</td>
                            <td className="p-2 text-right text-xs text-green-400 font-mono">₹{(paid/10000000).toFixed(2)}Cr</td>
                            <td className={`p-2 text-right text-xs font-mono ${remaining >= 0 ? 'text-white' : 'text-red-400'}`}>₹{(remaining/10000000).toFixed(2)}Cr</td>
                          </tr>
                        );
                      })}
                      {financeDetails.length === 0 && (
                        <tr><td colSpan={5} className="p-4 text-center text-gray-500 text-xs">No finance data yet.</td></tr>
                      )}
                    </tbody>
                  </table>

                  <div className="mt-4">
                    <button onClick={() => navigate('/finance')} className="w-full py-2 rounded text-white font-bold text-sm" style={{ backgroundColor: 'var(--brand-accent)' }}>
                      Open Finance Analytics →
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

    </div>
  );
}
